import papaParse from 'papaparse'

// "1,204"
export const number = (n) => (n || 0).toLocaleString()

// "96%" of the audiences a distribution sends - rounded down, so a gap never
// reads as 100%. None sent reads as "-".
export const percent = (part, whole) => (whole ? `${Math.floor((part / whole) * 100)}%` : '-')

// "Oct 14, 12:05 AM"
export const when = (date) =>
  date
    ? new Date(date).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : ''

// whether a row's distribution carries a percentile override
const hasOverride = (row) =>
  row.predictor_percentile_override !== null && row.predictor_percentile_override !== undefined

// "us", or "us · percentile 90"
export const mappingLabel = (row) =>
  hasOverride(row)
    ? `${row.mapping_id} · percentile ${row.predictor_percentile_override}`
    : row.mapping_id

// Not synced, as the pages show it: every audience a row counts but those
// synced - missing there (the API's not_synced), inactive, not checked, or
// with no ID to look for
export const notSyncedIn = (row) =>
  Math.max(0, (row.counts?.audiences || 0) - (row.counts?.synced || 0))

// anything to look at - not synced, or left off the distribution's allowlist.
// Nothing can be said of a partner not scanned yet.
export const gapsIn = (row) =>
  row.state === 'ok' ? notSyncedIn(row) + (row.counts?.not_allowed || 0) : 0

// Whether the partner has one left off an allowlist, in words: "In AppNexus:
// yes" - it decides the fix
const PARTNER_STATUS = {
  synced: 'yes',
  inactive: 'inactive',
  missing: 'no',
  unchecked: 'not checked',
}
export const partnerStatusOf = (audience) => PARTNER_STATUS[audience.partner_status] || '-'

// A new push for some of a row's audiences - those missing there, unless told
// which: its partner, the mapping ID a push fills it under (the API says
// which), and those audiences picked
export const prefillFor = (row, audienceIds = (row.not_synced || []).map((a) => a._id)) => ({
  partner: row.partner.slug,
  mapping_id: row.push_mapping_id,
  selection: {
    mode: 'ids',
    audience_ids: audienceIds,
    source: 'picker',
  },
})

// How many a push from the coverage page would send - every audience the
// partner does not have, but none on a blocklist (to_push, worked out by the
// API). A row stored before it was counted falls back to those missing.
export const toPushIn = (row) => row.counts?.to_push ?? row.counts?.not_synced ?? 0

// The audiences such a push picks, from a row worked out live - those
// missing there, for a row from before to_push was listed
export const toPushIdsOf = (row) => row.to_push || (row.not_synced || []).map((a) => a._id)

// Why a push cannot fill a row's gaps, when it cannot
export const noPushReason = (row) => {
  if (row.push_mapping_id) return null
  if (hasOverride(row)) {
    return "A push sends plain IDs, so it can't fill a distribution with a percentile override."
  }
  return `${row.partner.name} numbers the IDs in this space itself, so a push can't send them.`
}

const CSV_FIELDS = [
  'audience',
  'mongodb_id',
  'segment_id',
  'looked_for',
  'partner',
  'mapping_id',
  'state',
  'status',
  'allowlist',
]

// The audiences behind a row's gaps as a CSV - missing, inactive, not checked,
// not on the allowlist, then no ID - for taking to the partner before
// anything is pushed. `status` is the partner's: an inactive segment's state,
// or for one left off the allowlist, whether the partner has it.
export const coverageCsv = (row) => {
  const line = (state) => (audience) => ({
    audience: audience.name,
    mongodb_id: audience._id,
    segment_id: audience.segment_id || '',
    looked_for: (audience.looked_for || []).join(' '),
    partner: row.partner.name,
    mapping_id: mappingLabel(row),
    state,
    status: audience.status || '',
    // left off the distribution's allowlist - whatever the partner has
    allowlist: audience.off_allowlist ? 'not on it' : '',
  })
  const data = [
    ...(row.not_synced || []).map(line('missing')),
    ...(row.inactive || []).map(line('inactive')),
    ...(row.unchecked || []).map(line('not checked')),
    ...(row.not_allowed || []).map((audience) => ({
      ...line('not on allowlist')(audience),
      status: `in ${row.partner.name}: ${partnerStatusOf(audience)}`,
    })),
    ...(row.no_id || []).map(line('no ID')),
  ]
  return papaParse.unparse({ fields: CSV_FIELDS, data })
}

const REPORT_FIELDS = [
  'audience',
  'mongodb_id',
  'link',
  'mapping_id',
  'segment_id',
  'partner',
  'synced',
  'status',
  'allowlist',
]

// A row's report, from the coverage page: every audience in its taxonomy and
// where it stands in the partner - from a row asked for with every audience
// listed (getTaxonomyCoverage's audiences: 'all'). Those not synced first -
// missing, inactive, not checked, with no ID - then those synced, then those
// its blocklists leave out, which are not sent at all; each lot by name.
// `link` is the audience's page here, `segment_id` its ID in the row's mapping
// space (`mapping_id`), `status` where it stands - missing, inactive, synced
// and so on - and `allowlist` says when one is left off the distribution's.
export const coverageReportCsv = (row, origin = window.location.origin) => {
  const lines = (audiences, status, synced) =>
    [...(audiences || [])]
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map((audience) => ({
        audience: audience.name,
        mongodb_id: audience._id,
        link: `${origin}/audience/${audience._id}`,
        mapping_id: mappingLabel(row),
        segment_id: audience.segment_id || '',
        partner: row.partner.name,
        synced,
        status: audience.pushed_since ? `${status} - pushed since the scan` : status,
        allowlist: audience.off_allowlist ? 'not on it' : '',
      }))
  const data = [
    ...lines(row.not_synced, 'missing', 'no'),
    ...lines(row.inactive, 'inactive', 'no'),
    ...lines(row.unchecked, 'not checked', 'no'),
    ...lines(row.no_id, 'no ID', 'no'),
    ...lines(row.synced, 'synced', 'yes'),
    // never sent, so whether the partner has one is not asked
    ...lines(row.blocked, 'blocklisted', ''),
  ]
  return papaParse.unparse({ fields: REPORT_FIELDS, data })
}

// "Coverage - ShareThis Auto - AppNexus - us - 2026-10-14" (.csv added on
// download)
export const coverageCsvName = (taxonomyName, row) =>
  [
    'Coverage',
    taxonomyName,
    row.partner.name,
    mappingLabel(row).replace(' · ', ' '),
    new Date().toISOString().split('T')[0],
  ].join(' - ')

// "Coverage report - ShareThis Auto - AppNexus - us - 2026-10-14"
export const coverageReportName = (taxonomyName, row) =>
  coverageCsvName(taxonomyName, row).replace(/^Coverage/, 'Coverage report')
